import Route from '@ember/routing/route';
import { service } from '@ember/service';
import ENV from 'tio-employee/config/environment';
import { navigation } from 'tio-common/utils/navigation';
import type SessionService from 'tio-employee/services/session';
import type SessionContextService from 'tio-employee/services/session-context';
import { timeZone } from 'tio-common/utils/date/time-zone';
import { generateRequestId } from 'tio-common/utils/request-id';

export default class AuthByTokenRoute extends Route {
  @service declare session: SessionService;
  @service declare sessionContext: SessionContextService;

  async beforeModel() {
    try {
      await this.#completeHandoff();
    } catch (e) {
      console.error('SSO handoff failed:', e);
      // This route renders nothing but a spinner, so an unhandled rejection here
      // reads to the user as a login that never finishes. Send them somewhere
      // they can retry, dropping the spent token along with the hash.
      //
      // /logged-out rather than /login: the login route auto-redirects an SSO
      // tenant straight back to the IdP, which would loop us through the failing
      // handoff. /logged-out offers the same retry behind a deliberate click.
      //
      // Claiming isLoggingOut first is what keeps this to a single navigation:
      // sessionContext.load() tears the session down on failure, and the flag
      // suppresses the redirect the session service would otherwise fire.
      this.session.isLoggingOut = true;
      navigation.hardNavigate('/logged-out?sso_error=1');
    }
  }

  async #completeHandoff() {
    const tokens = Object.fromEntries(new URLSearchParams(location.hash.replace('#', '?')));

    const headers: Record<string, string> = {
      'x-api-key': ENV.apiKey,
      'tio-temp-token': tokens.tio as string,
      'Content-Type': 'application/json',
      'Time-Zone': timeZone(),
      'X-Request-Id': generateRequestId(),
    };
    if (this.session.publicKey) {
      headers['x-auth-key'] = this.session.publicKey;
    }

    const response = await fetch(`${ENV.apiHost}/saml/token_exchange`, { headers });
    // A non-2xx body is empty, so parsing it throws and leaves the expired temp
    // token in play — which then fails again, less legibly, against /me.
    if (!response.ok) {
      throw new Error(`Token exchange failed with status ${response.status}`);
    }
    const { access_token } = await response.json();

    await this.session.authenticate('authenticator:saml', { access_token });
    await this.sessionContext.load();
  }
}
